import { z } from "zod";

import {
  VibeAppDomainKindReadSchema,
  VibeAppDomainStatusReadSchema
} from "../../../../schemas/VibeAppDomain/status.schema";
import { unlistedDiscriminantSchema } from "../../../../shared/core/discriminated-union-read.schema";
import { enumReadSchema } from "../../../../shared/core/enum-read.schema";

/**
 * One DNS record the customer must create at their DNS provider for a custom
 * domain to point at the custom-domain edge. `name` is the full host the record
 * sits on, never a zone-relative label: providers disagree on how to spell the
 * apex (`@`, blank, the zone itself), and the full name is the one reading no
 * provider misinterprets.
 */
export const VibeAppDomainDnsRecordSchema = z.object({
  type: enumReadSchema(["CNAME", "A"] as const),
  name: z.string(),
  value: z.string()
});
export type VibeAppDomainDnsRecord = z.infer<typeof VibeAppDomainDnsRecordSchema>;

/**
 * What the customer is told to create, or why nothing can be told yet.
 *
 * `unavailable` exists for an APEX domain before the edge's static addresses
 * are configured: an apex cannot carry a CNAME, so the only honest instruction
 * is A records, and inventing addresses would send a customer's apex at an IP
 * that is not ours.
 */
export const VibeAppDomainDnsInstructionsSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ready"),
    records: z.array(VibeAppDomainDnsRecordSchema).min(1)
  }),
  z.object({
    status: z.literal("unavailable"),
    reason: z.string()
  })
]);
export type VibeAppDomainDnsInstructions = z.infer<typeof VibeAppDomainDnsInstructionsSchema>;

/**
 * The instructions as a READER parses them: a status a newer backend added reads
 * as the server's own word with every field it sent, instead of failing the
 * domains read.
 */
export const VibeAppDomainDnsInstructionsReadSchema = z.union([
  VibeAppDomainDnsInstructionsSchema,
  z.looseObject({ status: unlistedDiscriminantSchema(VibeAppDomainDnsInstructionsSchema) })
]);

/**
 * How many days a custom-domain claim may sit unproven before it is released.
 *
 * `host` is unique across every organization, so an unverified PENDING_DNS row
 * is a HOLD on a name: nobody else can attach it while the row exists. The
 * backend's `vibeAppDomainClaimExpiryCutoff` enforces it and owns the reasoning
 * for the figure; the dashboard and the CLI tell the owner about it.
 *
 * It lives on the CONTRACT rather than in the backend's value object because it
 * is a promise made to the customer, in a sentence, on two surfaces. A second
 * copy of the number in a locale file would go stale against the rule silently
 * — the copy would keep saying seven days after the rule moved.
 */
export const VIBE_APP_DOMAIN_PENDING_CLAIM_TTL_DAYS = 7;

export const VibeAppDomainSchema = z.object({
  id: z.string().uuid(),
  appId: z.string().uuid(),
  host: z.string(),
  kind: VibeAppDomainKindReadSchema,
  status: VibeAppDomainStatusReadSchema,
  statusReason: z.string().nullable(),
  verifiedAt: z.coerce.date().nullable(),
  activatedAt: z.coerce.date().nullable(),
  lastCheckedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  /** Whether this domain is its app's primary (canonical) host. */
  isPrimary: z.boolean(),
  dns: VibeAppDomainDnsInstructionsReadSchema
});
export type VibeAppDomainDto = z.infer<typeof VibeAppDomainSchema>;

/**
 * A host as it arrives in a path segment. Deliberately loose: the backend
 * canonicalises it with the same schema that wrote the stored hosts, so
 * `SHOP.Acme.COM.` addresses `shop.acme.com`, and a value that schema refuses
 * addresses nothing (404) rather than failing validation (400) — a caller holding
 * a host it cannot address learns the same thing either way.
 */
const HostPathSegmentSchema = z.string().min(1).max(260);

export const ListVibeAppDomainsPathVarsSchema = z.object({ appId: z.string().uuid() });
export const ListVibeAppDomainsResponseSchema = z.object({
  domains: z.array(VibeAppDomainSchema)
});

export const AddVibeAppDomainPathVarsSchema = z.object({ appId: z.string().uuid() });
/**
 * `host` is validated for length only here; the backend owns the real refusal
 * (`createCustomHostSchema` plus its deployed app domain as reserved), because
 * the reserved set is an env value no client can see.
 */
export const AddVibeAppDomainBodySchema = z.object({ host: z.string().min(1).max(260) });
export const AddVibeAppDomainResponseSchema = z.object({ domain: VibeAppDomainSchema });

export const DeleteVibeAppDomainPathVarsSchema = z.object({
  appId: z.string().uuid(),
  host: HostPathSegmentSchema
});
export const DeleteVibeAppDomainResponseSchema = z.object({ deletedHost: z.string() });

export const VerifyVibeAppDomainPathVarsSchema = z.object({
  appId: z.string().uuid(),
  host: HostPathSegmentSchema
});
export const VerifyVibeAppDomainResponseSchema = z.object({ domain: VibeAppDomainSchema });

export const SetVibeAppPrimaryDomainPathVarsSchema = z.object({ appId: z.string().uuid() });
/**
 * Name the new primary by its domain id — `null` clears it, and the app's
 * platform host is canonical again — or by its host.
 *
 * The host is taken as typed and canonicalised by the backend with the same
 * schema that wrote the stored hosts, so a client never keeps its own copy of
 * what a host is. A host that cannot be one is a 400; a host that is not a
 * domain of this app is a 404.
 *
 * Both shapes are `strict`: a body naming an id AND a host is refused rather
 * than silently resolved to one of them.
 */
export const SetVibeAppPrimaryDomainBodySchema = z.union([
  z.object({ domainId: z.string().uuid().nullable() }).strict(),
  z.object({ host: z.string().min(1).max(260) }).strict()
]);
export const SetVibeAppPrimaryDomainResponseSchema = z.object({
  primaryDomainId: z.string().uuid().nullable()
});
